import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Form, Pagination as BsPagination, Button } from 'react-bootstrap';
import Flex from './Flex';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Pagination = ({
  pageIndex = 0,
  pageSize,
  totalRecords = 0,
  currentCount = 0,
  onPageChange,
  onPageSizeChange,
  endOfResults = false,
  disabled = false,
  maxVisible = 5,
  size = 'sm',
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showSummary = true,
  showPageSize = true,
  className,
  onRefresh,
  loading
}) => {
  const effectiveTotal = totalRecords || currentCount;
  const totalPages = Math.max(1, Math.ceil((effectiveTotal || 0) / pageSize));
  const currentPage = pageIndex + 1;
  const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const goToPage = page => {
    if (disabled || page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    onPageChange(page - 1);
  };

  const commitPageInput = () => {
    const trimmed = String(pageInput).trim();
    if (!trimmed) {
      setPageInput(String(currentPage));
      return;
    }
    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(1, parsed), totalPages);
    setPageInput(String(clamped));
    goToPage(clamped);
  };

  const handlePageInputKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitPageInput();
      e.currentTarget.blur();
    }
  };

  const renderPageItems = () => {
    const items = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <BsPagination.Item
          key={1}
          onClick={() => goToPage(1)}
          disabled={disabled}
        >
          1
        </BsPagination.Item>
      );
      if (startPage > 2) {
        items.push(<BsPagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let p = startPage; p <= endPage; p++) {
      items.push(
        <BsPagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => goToPage(p)}
          disabled={disabled}
        >
          {p}
        </BsPagination.Item>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<BsPagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <BsPagination.Item
          key={totalPages}
          onClick={() => goToPage(totalPages)}
          disabled={disabled}
        >
          {totalPages}
        </BsPagination.Item>
      );
    }

    return items;
  };

  const isFirst = pageIndex <= 0;
  const isLast = endOfResults || pageIndex >= totalPages - 1;

  const rangeStart = effectiveTotal === 0 ? 0 : pageSize * pageIndex + 1;
  const rangeEnd = pageSize * pageIndex + currentCount;

  return (
    <Flex
      className={classNames(
        className,
        'align-items-center justify-content-between py-2'
      )}
    >
      <Flex alignItems="center" className="fs--1 flex-wrap gap-2">
        {showSummary && (
          <p className="mb-0">
            <span className="d-none d-sm-inline me-2">
              {rangeStart} to {rangeEnd} of {effectiveTotal}
            </span>
          </p>
        )}
        {showPageSize && onPageSizeChange && (
          <>
            <p className="mb-0 mx-2">Rows per page:</p>
            <Form.Select
              size={size}
              className="w-auto"
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              disabled={disabled}
            >
              {pageSizeOptions.map(n => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </>
        )}
        {onRefresh && (
          <Button
            variant="link"
            size="sm"
            className=""
            title="Refresh"
            onClick={onRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </Button>
        )}
      </Flex>
      <BsPagination size={size} className="mb-0">
        <BsPagination.First
          onClick={() => goToPage(1)}
          disabled={isFirst || disabled}
        />
        <BsPagination.Prev
          onClick={() => goToPage(currentPage - 1)}
          disabled={isFirst || disabled}
        />
        {/* {renderPageItems()} */}
        <li className="page-item d-flex align-items-center">
          <Form.Control
            type="number"
            min={1}
            max={totalPages}
            size={size}
            className="mx-1 text-center pagination-input"
            value={pageInput}
            onChange={e => setPageInput(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={handlePageInputKeyDown}
            disabled={disabled}
            aria-label={`Go to page, 1 to ${totalPages}`}
          />
        </li>
        <BsPagination.Next
          onClick={() => goToPage(currentPage + 1)}
          disabled={isLast || disabled}
        />
        <BsPagination.Last
          onClick={() => goToPage(totalPages)}
          disabled={isLast || disabled}
        />
      </BsPagination>
    </Flex>
  );
};

Pagination.propTypes = {
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number.isRequired,
  totalRecords: PropTypes.number,
  currentCount: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func,
  endOfResults: PropTypes.bool,
  disabled: PropTypes.bool,
  maxVisible: PropTypes.number,
  size: PropTypes.oneOf(['sm', 'lg']),
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  showSummary: PropTypes.bool,
  showPageSize: PropTypes.bool,
  className: PropTypes.string,
  onRefresh: PropTypes.func,
  loading: PropTypes.bool
};

export default Pagination;
